import { useAction } from "convex/react";
import { useCallback, useState } from "react";
import { api } from "@convex/_generated/api";

type AudioStatus = "idle" | "loading" | "playing" | "ready";

interface AudioState {
  status: AudioStatus;
  blobUrl?: string;
  audio?: HTMLAudioElement;
}

export function useTTS() {
  const vocaliserTexte = useAction(api.tts.vocaliserTexte);
  const [audioStates, setAudioStates] = useState<Map<number, AudioState>>(
    new Map(),
  );

  const handleVocalize = useCallback(
    async (idx: number, text: string) => {
      const current = audioStates.get(idx);

      if (current?.blobUrl && current.audio) {
        current.audio.currentTime = 0;
        await current.audio.play();
        return;
      }

      setAudioStates((prev) => {
        const next = new Map(prev);
        next.set(idx, { status: "loading" });
        return next;
      });

      try {
        const { audioBase64, mimeType } = await vocaliserTexte({ text });

        const binaryStr = atob(audioBase64);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) {
          bytes[i] = binaryStr.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);
        const audio = new Audio(blobUrl);

        audio.onended = () => {
          setAudioStates((prev) => {
            const next = new Map(prev);
            const state = next.get(idx);
            if (state) next.set(idx, { ...state, status: "ready" });
            return next;
          });
        };

        setAudioStates((prev) => {
          const next = new Map(prev);
          next.set(idx, { status: "playing", blobUrl, audio });
          return next;
        });

        await audio.play();
      } catch {
        setAudioStates((prev) => {
          const next = new Map(prev);
          next.set(idx, { status: "idle" });
          return next;
        });
      }
    },
    [audioStates, vocaliserTexte],
  );

  return { audioStates, handleVocalize };
}
